import { useState } from 'react';
import { Button, Label } from '@monksflow/monks-ui';
import { useDropzone } from 'react-dropzone';
import { UploadCloudIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { CREATIVE_EXTENSIONS } from '@/entities/media-campaign';
import { FileListItem } from '@/shared/ui/FileListItem';
import { formatFileSize } from '@/shared/utils/format';
import { BUNDLE_EXTENSIONS, isBundle, isPlan, isSidecar, PLAN_EXTENSIONS } from '../lib/limits';
import { splitSelection, type Refusal, type Selection } from '../lib/split-selection';

const ACCEPTED = [...PLAN_EXTENSIONS, ...BUNDLE_EXTENSIONS, ...CREATIVE_EXTENSIONS].join(',');

/** An archive belongs with the plan it holds, not with the creatives it is carrying. */
const isPlanFile = (file: File): boolean => isPlan(file) || isBundle(file);

/** Stable across removals, so pruning one row does not remount every row below it. */
const identityOf = (file: File): string => `${file.name}:${file.size}:${file.lastModified}`;

/**
 * The refusal wording, chosen by reason.
 *
 * Every message names the files it is about, which is why the field keeps no separate
 * "which file failed" state: a selection of several files needs the name inside the
 * sentence, not beside it.
 */
const messageFor = (refusal: Refusal, t: TFunction<'projects'>): string => {
  switch (refusal.reason) {
    case 'severalPlans':
      return t('deliveryJobCreate.newJobDialog.keepOnlyOnePlan', {
        fileNames: refusal.fileNames.join(', '),
      });
    case 'clashingNames':
      return t('deliveryJobCreate.newJobDialog.creativesShareAName', {
        fileNames: refusal.fileNames.join(', '),
      });
    case 'noPlan':
      return t('deliveryJobCreate.newJobDialog.planIsMissing');
    case 'bundleWithOthers':
      return t('deliveryJobCreate.newJobDialog.theZipGoesOnItsOwn');
    case 'unsupported':
      return t('deliveryJobCreate.newJobDialog.thoseAreNotCreatives', {
        fileNames: refusal.fileNames.join(', '),
      });
    case 'tooLarge':
      return refusal.files
        .map((file) =>
          t('deliveryJobCreate.newJobDialog.thatFileIsOverItsLimit', {
            fileName: file.fileName,
            limit: formatFileSize(file.limitBytes),
          }),
        )
        .join(' ');
  }
};

/**
 * Drops a file already staged, identified by name, size and mtime.
 *
 * "Add more files" opens an empty picker that shows nothing of what is already there, so
 * re-selecting the same folder is the ordinary thing to do rather than the odd one. Without
 * this, a second pass at a folder holding the plan produces a refusal naming the same file
 * twice — "keep one and remove the other" pointing at one file.
 */
const dedupe = (files: File[]): File[] => {
  const seen = new Set<string>();
  return files.filter((file) => {
    const identity = identityOf(file);
    if (seen.has(identity)) return false;
    seen.add(identity);
    return true;
  });
};

export interface PlanFilesFieldProps {
  /** Null whenever the current selection cannot be understood, which disables submit. */
  onChange: (selection: Selection | null) => void;
}

/**
 * The plan and the creatives it names, as one selection.
 *
 * A refused selection is kept on screen rather than discarded. The whole point of naming
 * two spreadsheets is that the operator can then remove one, which is impossible if the
 * refusal also clears what was dropped.
 */
export function PlanFilesField({ onChange }: PlanFilesFieldProps) {
  const { t } = useTranslation('projects');
  const [files, setFiles] = useState<File[]>([]);

  const result = files.length > 0 ? splitSelection(files) : null;
  const refusal = result && !result.ok ? result.refusal : null;

  const apply = (incoming: File[]) => {
    const next = dedupe(incoming.filter((file) => !isSidecar(file)));
    setFiles(next);

    const outcome = next.length > 0 ? splitSelection(next) : null;
    onChange(outcome?.ok ? outcome.selection : null);
  };

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    // Appends rather than replaces, so a second drop adds to what is already there.
    onDrop: (dropped) => apply([...files, ...dropped]),
    multiple: true,
    // The button below is the only click target; without this the whole zone opens the
    // picker too and a click on the button fires it twice.
    noClick: true,
    noKeyboard: true,
  });

  const group = (label: string, inGroup: File[]) =>
    inGroup.length > 0 && (
      <div className="flex flex-col gap-1.5">
        <span className="text-xs font-medium text-muted-foreground">{label}</span>
        {inGroup.map((file) => (
          <FileListItem
            key={identityOf(file)}
            fileName={file.name}
            fileSize={file.size}
            fileType={file.type}
            onRemove={() => apply(files.filter((kept) => kept !== file))}
          />
        ))}
      </div>
    );

  const creatives = files.filter((file) => !isPlanFile(file));

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor="new-job-files">{t('deliveryJobCreate.newJobDialog.files')}</Label>

      <div
        {...getRootProps()}
        className={
          files.length === 0
            ? `flex flex-col items-center gap-2 rounded-lg border border-dashed px-4 py-7 text-center ${
                isDragActive ? 'border-primary bg-muted' : 'border-border'
              }`
            : `flex flex-col gap-3 rounded-lg border border-dashed p-3 ${
                isDragActive ? 'border-primary bg-muted' : 'border-transparent'
              }`
        }
      >
        {files.length === 0 ? (
          <>
            <UploadCloudIcon className="size-6 text-muted-foreground" aria-hidden />
            <span className="text-sm text-muted-foreground">
              {t('deliveryJobCreate.newJobDialog.dropThePlanAndItsCreativesHere')}
            </span>
            <Button variant="outline" size="sm" onClick={open} data-testid="new-job-browse">
              {t('deliveryJobCreate.newJobDialog.chooseFiles')}
            </Button>
          </>
        ) : (
          <>
            {group(t('deliveryJobCreate.newJobDialog.plan'), files.filter(isPlanFile))}
            {group(
              t('deliveryJobCreate.newJobDialog.creativesWithCount', { count: creatives.length }),
              creatives,
            )}
            <Button
              variant="outline"
              size="sm"
              className="self-start"
              onClick={open}
              data-testid="new-job-add-more"
            >
              {t('deliveryJobCreate.newJobDialog.addMoreFiles')}
            </Button>
          </>
        )}
        <input
          id="new-job-files"
          {...getInputProps({ accept: ACCEPTED })}
          data-testid="new-job-file-input"
        />
      </div>

      {refusal && (
        <span role="alert" className="text-xs text-destructive" data-testid="new-job-error">
          {messageFor(refusal, t)}
        </span>
      )}

      <span className="text-xs text-muted-foreground">
        {t('deliveryJobCreate.newJobDialog.aFileInTheMonksTemplateWithCreatives')}
      </span>
    </div>
  );
}
